/**
 * HOOKS — ported from the aesthetic reference (§10, L1891–1996). Owner: S2.
 *
 *  MotionCtx          force reduced motion for a subtree (the reference board's "Preview" switch)
 *  useReducedMotion   OS setting OR MotionCtx OR the app store's `reducedMotion` flag
 *  reducedMotionNow   the same answer outside React (map layers, rAF loops)
 *  useLightField      raking light follows the pointer over .ld-rake; a ridge ripple on press
 *  useSize            element client size via ResizeObserver
 *  useIndicator       measures [data-key=activeKey] inside a container → box for <Sunrise/>
 *  useInView          true once the element has scrolled into view (latches)
 *  CSS_ESC            CSS.escape with a fallback, for [data-key="…"] selectors
 *
 * Everything guards window/document so it can be imported outside a browser.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
  type RefObject,
} from "react";
import { appStore } from "@/store/store";
import type { SunriseBox } from "./primitives";

const HAS_DOM = typeof window !== "undefined" && typeof document !== "undefined";
const useIsoLayoutEffect = HAS_DOM ? useLayoutEffect : useEffect;

/* ── reduced motion ─────────────────────────────────────────────────── */

export const MotionCtx = createContext(false);

const RM_QUERY = "(prefers-reduced-motion: reduce)";
let mql: MediaQueryList | null | undefined;
const mediaList = (): MediaQueryList | null => {
  if (mql === undefined) mql = HAS_DOM && typeof window.matchMedia === "function" ? window.matchMedia(RM_QUERY) : null;
  return mql;
};

function subscribeMedia(cb: () => void): () => void {
  const mq = mediaList();
  if (!mq) return () => {};
  if (typeof mq.addEventListener === "function") {
    mq.addEventListener("change", cb);
    return () => mq.removeEventListener("change", cb);
  }
  // Safari < 14
  mq.addListener(cb);
  return () => mq.removeListener(cb);
}
const getMedia = (): boolean => mediaList()?.matches ?? false;
const getFalse = (): boolean => false;

const subscribeStore = (cb: () => void): (() => void) => appStore.subscribe(cb);
const getStoreReduced = (): boolean => appStore.getState().reducedMotion;

/** Reduced motion right now, for code outside React (OS setting OR app store flag). */
export function reducedMotionNow(): boolean {
  return getMedia() || getStoreReduced();
}

/** true when prefers-reduced-motion, forced via MotionCtx, or the app store's reducedMotion flag is set. */
export function useReducedMotion(): boolean {
  const forced = useContext(MotionCtx);
  const media = useSyncExternalStore(subscribeMedia, getMedia, getFalse);
  const store = useSyncExternalStore(subscribeStore, getStoreReduced, getFalse);
  return forced || media || store;
}

/* ── light field ────────────────────────────────────────────────────── */

const rakeOf = (t: EventTarget | null): HTMLElement | null =>
  typeof Element !== "undefined" && t instanceof Element ? t.closest<HTMLElement>(".ld-rake") : null;

// Ripples are removed on animationend; this is the backstop if no animation runs.
const RIPPLE_MAX_MS = 1600;

/**
 * One delegated listener per root: raking light follows the pointer over any
 * .ld-rake element (sets --mx/--my), and a ridge ripple leaves the point of
 * contact on press. No ripple when root[data-motion="reduced"] or motion is reduced.
 */
export function useLightField(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    let raf = 0;
    let last: PointerEvent | null = null;
    const move = (e: PointerEvent) => {
      last = e;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const ev = last;
        if (!ev) return;
        const el = rakeOf(ev.target);
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty("--mx", `${ev.clientX - r.left}px`);
        el.style.setProperty("--my", `${ev.clientY - r.top}px`);
      });
    };
    const down = (e: PointerEvent) => {
      const el = rakeOf(e.target);
      if (!el || root.getAttribute("data-motion") === "reduced" || reducedMotionNow()) return;
      const r = el.getBoundingClientRect();
      const s = document.createElement("span");
      s.className = "ld-ripple";
      s.style.left = `${e.clientX - r.left}px`;
      s.style.top = `${e.clientY - r.top}px`;
      el.appendChild(s);
      const done = () => {
        window.clearTimeout(timer);
        s.remove();
      };
      const timer = window.setTimeout(done, RIPPLE_MAX_MS);
      s.addEventListener("animationend", done, { once: true });
    };
    root.addEventListener("pointermove", move, { passive: true });
    root.addEventListener("pointerdown", down, { passive: true });
    return () => {
      root.removeEventListener("pointermove", move);
      root.removeEventListener("pointerdown", down);
      cancelAnimationFrame(raf);
    };
  }, [ref]);
}

/* ── size ───────────────────────────────────────────────────────────── */

export function useSize(ref: RefObject<HTMLElement | null>): { w: number; h: number } {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useIsoLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const set = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setSize((s) => (s.w === w && s.h === h ? s : { w, h }));
    };
    set();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

/* ── sunrise indicator ──────────────────────────────────────────────── */

/** CSS.escape with a fallback, for attribute selectors built from data. */
export const CSS_ESC = (s: string): string =>
  typeof window !== "undefined" && window.CSS && typeof window.CSS.escape === "function" ? window.CSS.escape(s) : s.replace(/["\\]/g, "\\$&");

const sameBox = (a: SunriseBox | null, b: SunriseBox | null): boolean =>
  a === b || (!!a && !!b && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h);

/** Measures the active item ([data-key=activeKey] inside the container) and returns the box the sunrise moves to. */
export function useIndicator(containerRef: RefObject<HTMLElement | null>, activeKey: string | null): SunriseBox | null {
  const [box, setBox] = useState<SunriseBox | null>(null);
  const measure = useCallback(() => {
    const c = containerRef.current;
    if (!c) return;
    const el = activeKey == null ? null : c.querySelector<HTMLElement>(`[data-key="${CSS_ESC(String(activeKey))}"]`);
    const next = el ? { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight } : null;
    setBox((b) => (sameBox(b, next) ? b : next));
  }, [containerRef, activeKey]);
  useIsoLayoutEffect(() => {
    measure();
    const c = containerRef.current;
    if (!c || typeof ResizeObserver === "undefined") return undefined;
    let alive = true;
    const ro = new ResizeObserver(() => measure());
    ro.observe(c);
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
      void document.fonts.ready.then(() => {
        if (alive) measure();
      });
    }
    return () => {
      alive = false;
      ro.disconnect();
    };
  }, [measure, containerRef]);
  return box;
}

/* ── in view ────────────────────────────────────────────────────────── */

/** true once the element has entered the viewport (latches). Without IntersectionObserver it is true at once. */
export function useInView(ref: RefObject<HTMLElement | null>, margin = "-20% 0px"): boolean {
  const [seen, setSeen] = useState(() => HAS_DOM && typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const el = ref.current;
    if (!el || seen || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e && e.isIntersecting) setSeen(true);
      },
      { rootMargin: margin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, seen, margin]);
  return seen;
}
